import { Fragment, useRef, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { apiFetch } from "../api/client";
import { useNotification } from "../components/Notifications";
import { mapApiErrorToNotification } from "../utils/notificationHelpers";
import { formatNairaInput, parseNairaInput } from "../utils/currencyInput";

export default function AddSale({
    addSaleModalSetting,
    handlePageUpdate,
}) {
    const notify = useNotification();
    const cancelButtonRef = useRef(null);

    const [products, setProducts] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [cartItems, setCartItems] = useState([]);

    const [currentItem, setCurrentItem] = useState({
        productID: "",
        quantity: "",
        unitPrice: "",
    });

    const [paymentMethod, setPaymentMethod] = useState("Cash");

    // ---------------- FETCH PRODUCTS ----------------
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`/api/product`);
                if (!res.ok) return;

                const data = await res.json().catch(() => null);
                const arr = Array.isArray(data) ? data : (data?.data || data?.Data || []);

                setProducts(
                    (arr || []).map((p) => ({
                        id: p.productId || p.ProductId || p.id,
                        name: p.name || p.Name || p.productName || "",
                        salePrice: Number(p.salePrice ?? p.SalePrice ?? p.salesPrice ?? p.SalesPrice ?? 0),
                    }))
                );
            } catch (err) {
                console.error(err);
            }
        })();
    }, []);

    // ---------------- CART ----------------
    const addItemToCart = () => {
        if (!currentItem.productID || !currentItem.quantity) return;

        setCartItems((prev) => [
            ...prev,
            {
                productID: currentItem.productID,
                quantity: Number(currentItem.quantity),
                unitPrice: parseNairaInput(currentItem.unitPrice),
            },
        ]);

        setCurrentItem({ productID: "", quantity: "", unitPrice: "" });
    };

    const handleProductChange = (productId) => {
        const selectedProduct = products.find((p) => String(p.id) === String(productId));
        setCurrentItem({
            ...currentItem,
            productID: productId,
            unitPrice: selectedProduct?.salePrice ? formatNairaInput(selectedProduct.salePrice) : "",
        });
    };

    const removeItem = (index) => {
        setCartItems((prev) => prev.filter((_, i) => i !== index));
    };

    const totalAmount = cartItems.reduce(
        (sum, i) => sum + i.quantity * i.unitPrice,
        0
    );

    // ---------------- SUBMIT ----------------
    const completeSale = async () => {
        if (isSubmitting || cartItems.length === 0) return;

        setIsSubmitting(true);

        try {
            const payload = {
                Items: cartItems.map((i) => ({
                    ProductId: i.productID,
                    Quantity: i.quantity,
                    UnitPrice: i.unitPrice,
                })),
                PaymentMethod: paymentMethod,
            };

            const res = await apiFetch(`/api/sale`, {
                method: "POST",
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const parsed = await res.json().catch(() => null);
                notify("error", mapApiErrorToNotification(parsed, res.status));
                return;
            }

            notify("success", {
                title: "Sale completed",
                text: "Transaction saved successfully.",
            });

            handlePageUpdate();
            addSaleModalSetting();

        } catch (err) {
            notify("error", {
                title: "Network error",
                text: "Could not reach server.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Transition.Root show={true} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-50"
                initialFocus={cancelButtonRef}
                onClose={() => {}}
            >
                {/* BACKDROP */}
                <div className="fixed inset-0 bg-gray-700 bg-opacity-75" />

                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">

                        {/* MODAL PANEL */}
                        <Dialog.Panel className="w-full max-w-2xl bg-white rounded-xl shadow-xl p-6">

                            {/* HEADER */}
                            <div className="flex items-center gap-2 mb-4">
                                <PlusIcon className="h-6 w-6 text-blue-500" />
                                <h2 className="text-lg font-semibold">New Sale</h2>
                            </div>

                            {/* BODY */}
                            <div className="space-y-4">

                                <select
                                    value={currentItem.productID}
                                    onChange={(e) => handleProductChange(e.target.value)}
                                    className="w-full border p-2 rounded"
                                >
                                    <option value="">Select product</option>
                                    {products.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.name}
                                        </option>
                                    ))}
                                </select>

                                {currentItem.productID && (
                                    <div className="rounded-lg border border-purple-100 bg-purple-50 px-3 py-2 text-sm text-purple-900">
                                        Selling price: <span className="font-semibold">₦{parseNairaInput(currentItem.unitPrice).toLocaleString()}</span>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-3">
                                    <input
                                        type="number"
                                        placeholder="Quantity"
                                        value={currentItem.quantity}
                                        onChange={(e) =>
                                            setCurrentItem({ ...currentItem, quantity: e.target.value })
                                        }
                                        className="border p-2 rounded"
                                    />

                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="Unit Price"
                                        value={currentItem.unitPrice}
                                        onChange={(e) =>
                                            setCurrentItem({ ...currentItem, unitPrice: formatNairaInput(e.target.value) })
                                        }
                                        className="border p-2 rounded"
                                    />
                                </div>

                                <button
                                    onClick={addItemToCart}
                                    className="w-full bg-blue-600 text-white py-2 rounded"
                                >
                                    Add Item
                                </button>

                                {/* CART */}
                                <div>
                                    {cartItems.map((item, i) => {
                                        const product = products.find(p => String(p.id) === String(item.productID));

                                        return (
                                            <div className="flex justify-between border-b py-2 items-start">
                                        <div>
                                            <p className="font-medium">{product?.name}</p>

                                            <p className="text-xs text-gray-500">
                                                Qty: {item.quantity} × ₦{item.unitPrice.toLocaleString()}
                                            </p>
                                        </div>

                                        <div className="text-right">
                                            <p className="font-semibold">
                                                ₦{(item.quantity * item.unitPrice).toLocaleString()}
                                            </p>

                                            <button
                                                onClick={() => removeItem(i)}
                                                className="text-red-500 text-xs"
                                            >
                                                remove
                                            </button>
                                        </div>
                                    </div>
                                        );
                                    })}
                                </div>

                                <div className="font-bold">
                                    Total: ₦{totalAmount.toLocaleString()}
                                </div>

                                <select
                                    value={paymentMethod}
                                    onChange={(e) => setPaymentMethod(e.target.value)}
                                    className="w-full border p-2 rounded"
                                >
                                    <option value="Cash">Cash</option>
                                    <option value="POS">POS</option>
                                    <option value="Transfer">Transfer</option>
                                </select>

                            </div>

                            {/* FOOTER */}
                            <div className="flex justify-end gap-3 mt-5">

                                <button
                                    onClick={addSaleModalSetting}
                                    ref={cancelButtonRef}
                                    className="px-4 py-2 border rounded"
                                >
                                    Cancel
                                </button>

                                <button
                                    onClick={completeSale}
                                    disabled={isSubmitting || cartItems.length === 0}
                                    className="bg-green-600 text-white px-4 py-2 rounded disabled:opacity-50"
                                >
                                    {isSubmitting ? "Processing..." : "Complete Sale"}
                                </button>

                            </div>

                        </Dialog.Panel>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
